<template>
  <dx-data-grid
    ref="dataGridRef"
    :allow-column-reordering="true"
    :column-chooser="{ enabled: true, mode: 'select' }"
    :column-min-width="50"
    :columns="columns"
    :data-source="dataSource"
    :filter-row="{ visible: true }"
    :header-filter="{ visible: true }"
    :height="50"
    :hover-state-enabled="true"
    :remote-operations="{ filtering: true, paging: true, sorting: true, summary: true }"
    :repaint-changes-only="true"
    :row-alternation-enabled="true"
    :show-borders="true"
    :show-column-lines="true"
    :width="'100%'"
    :word-wrap-enabled="true"
    style="flex-grow: 1"
    value-expr="kod_sp_calc"
    @toolbar-preparing="onToolbarPreparing($event)"
    @cell-prepared="onCellPrepared($event)"
    @content-ready="onContentReady($event)"
  >
    <dx-paging :enabled="true" :page-size="40" />
    <dx-load-panel :enabled="true" />
    <dx-scrolling mode="virtual" />
    <dx-sorting mode="single" />
    <dx-export :allow-export-selected-data="true" :enabled="true" :formats="['pdf', 'excel']" />
    <dx-editing
      :allow-updating="!processGuid && isEditable"
      :refresh-mode="'repaint'"
      mode="batch"
    />

    <template #filterTemplatePeriod>
      <mped-calculation-period-component
        :default-period="'current'"
        @value-changed="onPeriodChanged($event)"
        :default-value="defaultValue"
      />
    </template>

    <template #filterTemplateOdNd>
      <dx-drop-down-box
        :data-source="vidDeyKodList"
        :defer-rendering="false"
        :display-expr="'name'"
        :drop-down-options="{ width: 250 }"
        :show-clear-button="true"
        :value="vidDeyKod"
        :width="'100%'"
        placeholder="Вид деятельности"
        value-expr="id"
        @value-changed="dropDownBoxValueChanged($event)"
      >
        <template>
          <dx-data-grid
            v-model:selected-row-keys="vidDeyKod"
            :data-source="vidDeyKodList"
            :height="'auto'"
            :hover-state-enabled="true"
            :show-column-headers="false"
            key-expr="id"
          >
            <dx-selection mode="multiple" />
            <dx-scrolling mode="virtual" />
            <dx-column
              dataField="name"
            />
          </dx-data-grid>
        </template>
      </dx-drop-down-box>
    </template>

    <template #filterTemplateError>
      <dx-check-box
        v-model:value="hasError"
        text="Данные с ошибками"
        @value-changed="() => { reloadData() }" />
    </template>

    <template #editableCellTemplate="{ data }">
      <div class="d-flex">
        <div class="flex-grow-1">{{ data.value }}</div>
        <i v-if="!processGuid" class="dx-icon-edit text-success text-opacity-50"></i>
      </div>
    </template>

    <template #captionGuidTemplate>
      <div class="fw-bold">
        {{captionGuid}}
      </div>
    </template>

    <dx-summary>
      <dx-total-item
        v-for="(item) in summaryColumns"
        :key="item.dataField"
        :column="item.dataField"
        :name="item.dataField"
        :show-in-column="item.dataField"
        :value-format="numberFormat"
        display-format="{0}"
        summary-type="sum"
      />
    </dx-summary>
  </dx-data-grid>

  <footer-grid :show-selected-counter="false" :totalCount="totalCount" class="px-2"></footer-grid>
</template>

<script>
export default {
  name: 'mped-calculation-upload-view-view',
};
</script>

<script setup>
import { ref, onBeforeMount, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import {
  DxDataGrid,
  DxScrolling,
  DxLoadPanel,
  DxSorting,
  DxExport,
  DxPaging,
  DxSelection,
  DxColumn,
  DxSummary,
  DxTotalItem,
  DxEditing,
} from 'devextreme-vue/data-grid';
import { DxCheckBox } from 'devextreme-vue';
import { DxDropDownBox } from 'devextreme-vue/drop-down-box';

// import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import MpedCalculationPeriodComponent
  from '@/views/mped/views/calculation/component/mped-calculation-period-component.vue';
import { columnsCalculationSap } from '@/views/mped/services/mped-calculation-service';
import { checkHasEditData, getAiToolbar, undoEditData } from '@/components/utils/ai-toolbar-generator';
import { createFilterArray, getAiDataSource } from '@/components/utils/ai-data-source-generator';
import { getUploadedSapData, postCalculationRow } from '@/views/mped/services/mped-calculation-api-service';
import { getCountrySubjects } from '@/views/mped/services/mped-department-service';
import FooterGrid from '@/components/grid-card-component/footer-grid.vue';
import { numberFormat } from '@/views/mped/services/mped-columns-service';
import { getLocalUserdata, initState, setLocalUserdata, hasEditablePermissionByUIRoute } from '@/service/local-userdata-service';


const totalCount = ref(0);
const periodsValue = ref([]);
const columns = ref();
const summaryColumns = ref();
const vidDeyKod = ref([]);
const hasError = ref(false);
const vidDeyKodList = [{ id: 0, name: 'ОД' }, { id: 1, name: 'НД' }, { id: -1, name: 'Пусто' }];
const dataGridRef = ref();
const route = useRoute();
const processGuid = route.params.guid;
const captionGuid = route.params.caption;
const router = useRouter();
const isEditable = hasEditablePermissionByUIRoute(router);

let
  toolbarButtons = {},
  defaultValue = null,
  firstLoad = true;

const dataSource = getAiDataSource(
  {
    dataFn: getUploadedSapData,
    key: 'kod_sp_calc',
    remoteOperation: true,
    validationFn: () => {
      return processGuid || periodsValue.value && periodsValue.value.length;
    },
    customFilterFn: () => {
      if (processGuid)
        return ['process_guid', '=', processGuid];
      let errorFilter;
      let vidDeyFilter;
      const periodFilter = createFilterArray(periodsValue.value.reduce((total, item) => {
        total.push(['period', '=', Number(item)]);
        return total;
      }, []), 'or');
      if (hasError.value) {
        errorFilter = createFilterArray([
            ['el_zatr_kod', '=', null],
            ['mb3_kod_account', '=', null],
            ['kod_spr_smet_zatr', '=', null]],
          'or');
      }
      if (vidDeyKod.value?.length) {
        vidDeyFilter =
          createFilterArray(vidDeyKod.value.reduce((total, item) => {
            let value = Number(item);

            if (item === -1) {
              value = null;
            }

            total.push(['od_nd_kod', '=', value]);
            return total;
          }, []), 'or');
      }

      return createFilterArray([periodFilter, errorFilter, vidDeyFilter]);
    },
    onLoadedFn: (data) => {
      totalCount.value = data.totalCount;
    },
    paramsFn: () => {
      return {
        processGuid,
        summary: summaryColumns.value?.reduce((total, item) => {
          total[item.dataField] = item.summary;
          return total;
        }, {}),
      };
    },
    updateFn: (key, values) => {
      return postCalculationRow({ key, values });
    },
  },
);

onBeforeMount(async () => {
  const countrySubjects = await getCountrySubjects();
  columns.value = columnsCalculationSap(false, {
    countrySubjects: {
      valueExpr: 'kod_subj',
      displayExpr: 'kod_subj',
      dataSource: countrySubjects,
    }
  }, isEditable);
  summaryColumns.value = columns.value.filter(c => c.summary);
  if (processGuid)
    document.title = `Детализация сводного отчета №${processGuid}`;
  else {
    const state = getLocalUserdata(router);
    defaultValue = state?.filters?.periods;
    vidDeyKod.value = state?.filters.vidDeyKod || [];
    hasError.value = state?.filters.hasError || false;
    firstLoad = false;
  }
});

onMounted(async () => {
  initState(router, dataGridRef);
});

const onToolbarPreparing = (e) => {

  const filters = [{
    location: 'before',
    template: 'filterTemplateError',
  }];

  if (!processGuid) {
    filters.unshift({
      location: 'before',
      template: 'filterTemplatePeriod',
    }, {
      location: 'before',
      template: 'filterTemplateOdNd',
    });
  }

  const buttons = {
    undo: {
      event: (e) => {
        undoEditData(dataGridRef.value.instance);
      },
    },
    save: {
      event: (e) => {
        dataGridRef.value.instance.saveEditData();
      },
    },
  };

  toolbarButtons = getAiToolbar(e, {
    customs: !processGuid && filters || [{
      location: 'before',
      template: 'captionGuidTemplate',
    }],
    refresh: {
      event: reloadData,
    },
    ...(!processGuid && buttons || {}),
  });
};

const onContentReady = (e) => {
  checkHasEditData(dataGridRef.value.instance, toolbarButtons);

  if (!firstLoad)
    setState();
};

const setState = () => {
  setLocalUserdata(router, dataGridRef, {
    periods: periodsValue.value || [],
    vidDeyKod: vidDeyKod.value,
    hasError: hasError.value,
  });
}

const reloadData = () => {
  dataSource.reload();
  setState();
};

const onPeriodChanged = (e) => {
  periodsValue.value = e;
  reloadData();
};

const onCellPrepared = (e) => {
  if (e.rowType === 'data') {
    const { mb3_kod_account, el_zatr_kod, kod_spr_smet_zatr } = e.row.data;
    if (!el_zatr_kod)
      e.cellElement.classList.add('calculation-text-empty-zatr');
    if ([null, undefined].includes(mb3_kod_account))
      e.cellElement.classList.add('calculation-text-empty-account');
    if (!kod_spr_smet_zatr && el_zatr_kod)
      e.cellElement.classList.add('calculation-text-not-equal');
  }
};

const dropDownBoxValueChanged = (e) => {
  if (!e.value) {
    vidDeyKod.value = [];
  }
  reloadData();
};

</script>

<style>
.calculation-text-empty-zatr {
  color: #a29123 !important;
}

.calculation-text-empty-account {
  color: #3666b6 !important;
}

.calculation-text-not-equal {
  color: #c52c2c !important;
}
</style>

<style scoped>

</style>
